import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";

export type BillingPeriod = "monthly" | "yearly";

@customElement("pricing-component")
export class PricingComponent extends LitElement {
    @property({ type: Number }) monthlyPrice: number = 19;
    @property({ type: Number }) yearlyPrice: number = 190;
    @property({ type: String }) buttonTitle: string = "Start 14-Day Free Trial";
    @property({ type: String }) billingPeriod: BillingPeriod = "monthly";

    createRenderRoot() {
        return this;
    }

    private get effectiveMonthlyPrice(): number {
        return Math.round(this.yearlyPrice / 12);
    }

    private selectBillingPeriod(period: BillingPeriod) {
        this.billingPeriod = period;
    }

    private handleButtonClick() {
        this.dispatchEvent(
            new CustomEvent("pricing-button-click", {
                detail: { billingPeriod: this.billingPeriod },
                bubbles: true,
                composed: true,
            })
        );
    }

    private renderToggle() {
        const isMonthly = this.billingPeriod === "monthly";
        return html`
            <div class="flex flex-col items-center mb-6 mt-8">
                <div class="relative inline-flex flex-col items-center">
                    <!-- "2 months free" annotation above Yearly -->
                    <div class="absolute -top-4 right-8 flex -translate-y-full items-center gap-1">
                        <span class="whitespace-nowrap text-sm font-medium text-success md:text-base italic">2 months free</span>
                        <svg
                            class="mt-3 w-6 rotate-[32deg] -scale-x-100 fill-success opacity-60 md:w-8"
                            viewBox="0 0 219 41"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M21.489 29.4305C36.9333 31.3498 51.3198 33.0559 65.7063 34.9753C66.7641 35.1885 67.6104 36.4681 69.9376 38.3875C63.1675 39.2406 57.8783 40.3069 52.5892 40.5201C38.6259 40.9467 24.8741 40.9467 10.9107 40.9467C9.21821 40.9467 7.5257 41.1599 5.83317 40.7334C0.332466 39.6671 -1.57164 36.0416 1.39028 31.1365C2.87124 28.7906 4.56377 26.658 6.46786 24.7386C13.6611 17.4876 21.0659 10.4499 28.4707 3.41224C29.7401 2.13265 31.6442 1.49285 34.183 0C34.6061 10.8765 23.8162 13.8622 21.489 22.3927C23.3931 21.9662 25.0856 21.7529 26.5666 21.3264C83.6894 5.54486 140.601 7.25099 197.3 22.606C203.224 24.0988 208.936 26.4447 214.649 28.5773C217.61 29.6437 220.149 31.9896 218.457 35.6151C216.976 39.2406 214.014 39.2406 210.629 37.7477C172.759 20.6866 132.561 18.7672 91.9404 19.407C70.7838 19.6203 50.0504 21.9662 29.5285 26.8713C26.9897 27.5111 24.4509 28.3641 21.489 29.4305Z"
                            ></path>
                        </svg>
                    </div>
                    <div class="join border border-base-300 rounded-lg">
                        <button
                            class="join-item btn btn-sm no-animation ${isMonthly ? "btn-primary" : "btn-ghost"}"
                            @click=${() => this.selectBillingPeriod("monthly")}
                        >
                            Monthly
                        </button>
                        <button
                            class="join-item btn btn-sm no-animation ${!isMonthly ? "btn-primary" : "btn-ghost"}"
                            @click=${() => this.selectBillingPeriod("yearly")}
                        >
                            Yearly
                        </button>
                    </div>
                </div>
            </div>
        `;
    }

    private renderPrice() {
        if (this.billingPeriod === "yearly") {
            return html`
                <div class="flex flex-col min-h-[5rem]">
                    <div class="flex items-baseline">
                        <span class="mr-2 text-6xl font-extrabold">$${this.yearlyPrice}</span>
                        <span class="text-gray-500">/year</span>
                    </div>
                    <span class="text-sm text-gray-500 mt-1">$${this.effectiveMonthlyPrice}/mo · billed annually</span>
                </div>
            `;
        }
        return html`
            <div class="flex items-baseline min-h-[5rem]">
                <span class="mr-2 text-6xl font-extrabold">$${this.monthlyPrice}</span>
                <span class="text-gray-500">/month</span>
            </div>
        `;
    }

    private renderFeatureItem(title: string, description: string) {
        return html`
            <li class="flex space-x-3">
                <svg class="shrink-0 w-5 h-5 text-green-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
                <div class="flex flex-col">
                    <span class="font-semibold">${title}</span>
                    <span class="text-sm text-gray-500">${description}</span>
                </div>
            </li>
        `;
    }

    render() {
        return html`
            <div class="flex flex-col mx-auto max-w-lg">
                ${this.renderToggle()}

                <div class="flex flex-col space-y-4 p-6 rounded-3xl border border-gray-200">
                    <p class="font-light text-gray-500 sm:text-lg">Everything you need to grow your App Store presence.</p>

                    ${this.renderPrice()}

                    <ul role="list" class="mb-8 space-y-4 text-left">
                        ${this.renderFeatureItem("Keywords Popularity & Difficulty", "Find high ranking keywords from Apple Search Ads")}
                        ${this.renderFeatureItem("Keyword Suggestions", "Discover untapped keyword opportunities")}
                        ${this.renderFeatureItem("Keyword Lists", "See your app rank for all relevant keywords")}
                        ${this.renderFeatureItem("Keyword Rankings", "Rank charts and competing apps for any keyword")}
                        ${this.renderFeatureItem("API & CLI", "Automate keyword discovery and research")}
                        ${this.renderFeatureItem("90 Countries", "Find worldwide App Store opportunities")}
                    </ul>

                    <button class="btn btn-primary w-full" @click=${() => this.handleButtonClick()}>${this.buttonTitle}</button>
                    <p class="text-sm text-gray-500 text-center -mt-2">Cancel anytime. No commitment.</p>
                </div>
            </div>
        `;
    }
}
